import React, { createContext, useContext, ReactNode } from 'react';
import { useAllPhaseGuidance } from '../hooks/useAllPhaseGuidance';
import { InternshipPhase } from '../components/InternshipDashboard/InternshipDashboardV2';
import { PhaseGuidance } from '../hooks/usePhaseGuidance';

interface PhaseGuidanceContextType {
  getPhaseGuidance: (phase: InternshipPhase) => PhaseGuidance | null;
  allGuidance: Record<InternshipPhase, PhaseGuidance> | null;
  loading: boolean;
  error: Error | null;
}

const PhaseGuidanceContext = createContext<PhaseGuidanceContextType | undefined>(undefined);

interface PhaseGuidanceProviderProps {
  children: ReactNode;
  industryType: string | null | undefined;
}

export function PhaseGuidanceProvider({ children, industryType }: PhaseGuidanceProviderProps) {
  const { guidance, loading, error } = useAllPhaseGuidance(industryType);

  const getPhaseGuidance = (phase: InternshipPhase): PhaseGuidance | null => {
    if (!guidance) return null;
    return guidance[phase] || null;
  };

  return (
    <PhaseGuidanceContext.Provider
      value={{
        getPhaseGuidance,
        allGuidance: guidance,
        loading,
        error
      }}
    >
      {children}
    </PhaseGuidanceContext.Provider>
  );
}

export function usePhaseGuidanceContext() {
  const context = useContext(PhaseGuidanceContext);
  if (context === undefined) {
    throw new Error('usePhaseGuidanceContext must be used within a PhaseGuidanceProvider');
  }
  return context;
}

// Hook to get guidance for a specific phase from context
export function usePhaseGuidanceFromContext(phase: InternshipPhase) {
  const { getPhaseGuidance, loading, error } = usePhaseGuidanceContext();
  const guidance = getPhaseGuidance(phase);
  
  return { guidance, loading, error };
}